import { useEffect, useId, useState } from "react";

import { css, cx } from "@aphrodite/css";
import { ANALYTICS_PROPERTIES, AnalyticsEvents } from "~/constant/analytics-events";
import { ROUTES } from "~/constant/routes";
import { usePublicPageView } from "~/hooks/use-track-page-view";
import { useLoggedOutTracking } from "~/hooks/use-tracking";
import { MagicLinkSent } from "~/modules/auth/components/magic-link-sent";
import { AuthQueries } from "~/modules/auth/queries";
import { Box, Flex, ResponsiveBox } from "~/primitives/box";
import { FormServerError } from "~/primitives/form/errors";
import { Form } from "~/primitives/form/Form";
import { SubmitButton } from "~/primitives/form/submit-button";
import { FloatingInput } from "~/primitives/input/floating-input";
import { Link } from "~/primitives/link";
import { Text } from "~/primitives/text";
import { ToastAction } from "~/primitives/toast";

import { styles } from "./creator-sign-up";
import { creatorAuthSchema, TCreatorAuthFormData } from "./schemas/creator-auth";

type TLoginPageProps = {
  isMagicLinkInvalid?: boolean;
};

export const CreatorLogin = ({ isMagicLinkInvalid }: TLoginPageProps) => {
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const { trackEvent } = useLoggedOutTracking();

  usePublicPageView(AnalyticsEvents.CREATOR_TOOL_PAGE_VIEWED, {
    trackEvent,
  });

  const onSubmit = async ({ email }: TCreatorAuthFormData): Promise<void> => {
    return new Promise((resolve, reject) => {
      AuthQueries.sendMagicLink(email)
        .then(() => {
          trackEvent(AnalyticsEvents.SIGN_IN_CLICKED_STEP2, {
            [ANALYTICS_PROPERTIES.SIGN_IN_TYPE]: "email",
          });
          setSubmittedEmail(email);
          resolve();
        })
        .catch(() => {
          ToastAction.showError("Something went wrong, please try again");
          reject(new FormServerError({ type: "INTERNAL_SERVER_ERROR", errors: null }));
        });
    });
  };

  useEffect(() => {
    if (isMagicLinkInvalid) {
      ToastAction.showError("The link you're using seems to have expired.");
    }
  }, []);

  return (
    <Box className={styles.container}>
      <Form
        formConfig={{ mode: "onBlur" }}
        schema={creatorAuthSchema}
        onSubmit={onSubmit}
        className={cx(
          styles.formContainer,
          css({
            visibility: submittedEmail ? "hidden" : "visible",
          }),
        )}
      >
        <LoginForm />
      </Form>
      {submittedEmail && <MagicLinkSent isLogin submittedEmail={submittedEmail} />}
    </Box>
  );
};

const LoginForm = () => {
  const id = useId();
  return (
    <Box className={styles.signup}>
      <Flex flexDirection="column" gap="space.8">
        <Text className={styles.heading} as="h1">
          Log in to your account
        </Text>
        <Text className={styles.subHeading} id={id}>
          We’ll send a link to your email that will instantly sign you in
        </Text>
      </Flex>

      <Box className={styles.form}>
        <FloatingInput name="email" label="Registered email address" aria-describedby={id} />
        <ResponsiveBox viewMode="desktop">
          <DoesntHaveAccount />
        </ResponsiveBox>
        <Box className={styles.buttonContainer}>
          <SubmitButton text="Get log in link" />
        </Box>
        <ResponsiveBox viewMode="belowDesktop">
          <DoesntHaveAccount />
        </ResponsiveBox>
      </Box>
    </Box>
  );
};

const DoesntHaveAccount = () => {
  const { trackEvent } = useLoggedOutTracking();

  return (
    <Box display="flex" alignItems="center" gap={8}>
      <Text className={styles.loginPretext} textStyle="Semantics/UI Label/Medium">
        Don’t have an account yet?
      </Text>
      <Link
        size="medium"
        href={ROUTES.PUBLIC_CREATOR_SIGNUP}
        heavy
        linkType="oceanblue"
        text="Sign up"
        onClick={() => {
          trackEvent(AnalyticsEvents.CREATOR_TOOL_CTA_CLICKED, {
            [ANALYTICS_PROPERTIES.CTA_TYPE]: "Sign Up",
          });
        }}
      />
    </Box>
  );
};
